import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { ImagePlus } from "lucide-react";
import { AccessPoint, LayoutPin } from "../types";
import { passtrackColors } from "../theme";
import {
  clickToImagePercent,
  getContainedImageRect,
} from "../utils/layoutImage";

interface LayoutImageCanvasProps {
  image: string;
  pins: LayoutPin[];
  accessPoints: AccessPoint[];
  selectedPointId: string | null;
  onSelectPoint: (id: string) => void;
  onPlacePin: (accessPointId: string, xPercent: number, yPercent: number) => void;
  onUploadClick: () => void;
}

export default function LayoutImageCanvas({
  image,
  pins,
  accessPoints,
  selectedPointId,
  onSelectPoint,
  onPlacePin,
  onUploadClick,
}: LayoutImageCanvasProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [image]);

  if (!image) {
    return (
      <Box
        component="button"
        type="button"
        onClick={onUploadClick}
        sx={{
          flex: 1,
          minHeight: 360,
          width: "100%",
          borderRadius: 2,
          border: `1.5px dashed ${passtrackColors.blue200}`,
          background: passtrackColors.mist,
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.25,
          p: 3,
          font: "inherit",
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            background: passtrackColors.cloud,
            color: passtrackColors.blue,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ImagePlus size={22} strokeWidth={2} />
        </Box>
        <Typography
          sx={{ fontWeight: 600, fontSize: 14, color: passtrackColors.ink }}
        >
          Subir layout
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            color: passtrackColors.textMuted,
            textAlign: "center",
            maxWidth: 280,
          }}
        >
          PNG, JPG o SVG. Usa un plano o croquis de la propiedad como referencia
          visual.
        </Typography>
      </Box>
    );
  }

  const imageRect = getContainedImageRect(
    containerSize.width,
    containerSize.height,
    naturalSize.width,
    naturalSize.height,
  );

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!selectedPointId || !containerRef.current) return;
    if ((e.target as HTMLElement).closest("[data-layout-pin]")) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const point = clickToImagePercent(
      e.clientX,
      e.clientY,
      containerRect,
      imageRect,
    );
    if (!point) return;

    onPlacePin(selectedPointId, point.xPercent, point.yPercent);
  };

  return (
    <Box
      ref={containerRef}
      onClick={handleClick}
      sx={{
        flex: 1,
        minHeight: 360,
        width: "100%",
        borderRadius: 2,
        border: `1.5px dashed ${passtrackColors.borderDefault}`,
        background: "#fff",
        position: "relative",
        overflow: "hidden",
        cursor: selectedPointId ? "crosshair" : "default",
        userSelect: "none",
      }}
    >
      <Box
        component="img"
        ref={imgRef}
        src={image}
        alt="Layout de la propiedad"
        onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
          const img = e.currentTarget;
          setNaturalSize({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        }}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
          pointerEvents: "none",
        }}
      />

      {pins.map((pin) => {
        const point = accessPoints.find((p) => p.id === pin.accessPointId);
        const index = accessPoints.findIndex((p) => p.id === pin.accessPointId);
        const selected = pin.accessPointId === selectedPointId;
        if (index < 0) return null;

        const label =
          point?.nombre?.trim() || `Punto de acceso ${index + 1}`;
        const left =
          imageRect.left + (pin.xPercent / 100) * imageRect.width;
        const top = imageRect.top + (pin.yPercent / 100) * imageRect.height;

        return (
          <Box
            key={pin.accessPointId}
            data-layout-pin
            component="button"
            type="button"
            title={label}
            onClick={(e) => {
              e.stopPropagation();
              onSelectPoint(pin.accessPointId);
            }}
            sx={{
              position: "absolute",
              left,
              top,
              transform: "translate(-50%, -100%)",
              border: "none",
              background: "transparent",
              p: 0,
              cursor: "pointer",
              zIndex: selected ? 2 : 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                maxWidth: 140,
                px: 1,
                py: 0.35,
                borderRadius: 99,
                background: selected
                  ? passtrackColors.blue
                  : "rgba(255,255,255,.94)",
                border: `1px solid ${
                  selected ? passtrackColors.blue : passtrackColors.borderSubtle
                }`,
                boxShadow: "0 2px 8px rgba(10,42,107,.12)",
              }}
            >
              <Typography
                noWrap
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  color: selected ? "#fff" : passtrackColors.ink,
                }}
              >
                {label}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50% 50% 50% 0",
                transform: "rotate(-45deg)",
                background: selected
                  ? passtrackColors.blue
                  : passtrackColors.green,
                border: "2px solid #fff",
                boxShadow: "0 4px 10px rgba(10,42,107,.28)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="span"
                sx={{
                  transform: "rotate(45deg)",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {index + 1}
              </Box>
            </Box>
          </Box>
        );
      })}

      <Box
        sx={{
          position: "absolute",
          left: "50%",
          bottom: 12,
          transform: "translateX(-50%)",
          px: 1.5,
          py: 0.75,
          borderRadius: 99,
          background: "rgba(255,255,255,.92)",
          border: `1px solid ${passtrackColors.borderSubtle}`,
          boxShadow: "0 2px 8px rgba(10,42,107,.08)",
          pointerEvents: "none",
        }}
      >
        <Typography sx={{ fontSize: 12, color: passtrackColors.textMuted }}>
          {selectedPointId
            ? "Haz clic en el layout para colocar el pin del punto seleccionado"
            : "Selecciona un punto para colocar su pin"}
        </Typography>
      </Box>
    </Box>
  );
}
